import { ActivationTypes } from '@cbo/shared-library/types/activation';
import { useMemo } from 'react';
import { isNil } from 'lodash';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import {
  EmployeeBreakReportCommonRequestSchema,
  IntervalSalesAndLaborReportCommonRequestSchema,
  EmployeeSalesPerformanceCommonRequest,
  ApproachingACAHourlyThresholdGridDataRequest,
  JobcodeHistoryGridRequest,
  OvertimeThresholdCommonRequest,
  EmployeePayrollDto,
  GetEmployeesRequestSchema,
  GetPaginatedJobsRequest,
} from '../types';
import { useUsers } from '../../contexts/userContext';
import { BaseReportDataRequest } from '../../../api/sales/requests';

const useLaborQueryKeys = () => {
  const { siteId } = useCurrentSite();

  const { firebaseToken, org } = useUsers();
  // Query keys dependant on EUID updates:
  const queryKeys = useMemo(() => {
    const jobcodeKeys = {
      all: ['job-code-definitions'] as const,
      bySite: [siteId, firebaseToken] as const,
      list: (query?: GetPaginatedJobsRequest) => [...jobcodeKeys.all, ...jobcodeKeys.bySite, query],
      listByStatus: (activationStatus: ActivationTypes) => [...jobcodeKeys.all, activationStatus],
      listById: (id: string) => [...jobcodeKeys.all, id],
      byId: (id: string) => [id],
      jobListByIds: (jobcodes: string[]) => ['jobs', jobcodes.map((jobCode) => jobCode)],
      paginatedList: (page: number, pageSize: number) => [...jobcodeKeys.all, page, pageSize],
      allJobs: (query?: GetPaginatedJobsRequest) =>
        [...jobcodeKeys.all, ...jobcodeKeys.bySite, 'all-jobs', query] as const,
    };

    const laborGroupKeys = {
      all: ['labor-groups', siteId, firebaseToken] as const,
      listById: (id: string) => [...laborGroupKeys.all, id],
    };

    return {
      jobcode: jobcodeKeys,
      laborGroup: laborGroupKeys,
    };
  }, [siteId, firebaseToken]);

  // Non EUID-dependent queries:
  const employeeKeys = {
    all: ['employees', firebaseToken] as const,
    byEmpId: (id: string) => [...employeeKeys.all, id],
    listByIdSelf: () => [...employeeKeys.all, 'self' as const],
    listByIds: (empIds: string[]) => ['employees', empIds.sort((a, b) => a.localeCompare(b))],
    listByFilters: (filters: GetEmployeesRequestSchema) => {
      const returnKeys = ['employees', firebaseToken];
      Object.entries(filters).forEach(([key, val]) => {
        if (!isNil(val)) {
          if (key === 'employeeIds') {
            returnKeys.push(...val.sort((a: string, b: string) => a.localeCompare(b)));
          } else {
            returnKeys.push(val);
          }
        }
      });
      return returnKeys;
    },
  };

  // keys for new asm labor service to get employee self details
  const employeeDetailsKeys = {
    all: ['employee-details'] as const,
    self: () => ['self' as const, ...employeeDetailsKeys.all],
  };

  const employeeJobCodeAssignmentKeys = {
    all: ['employee-job-code-assignments'] as const,
    listById: (id: string) => [...employeeJobCodeAssignmentKeys.all, id],
  };

  const employeeTipReportKeys = {
    all: ['employee-tip'] as const,
    page: (apiPath: string, request: EmployeeBreakReportCommonRequestSchema) => [
      ...employeeTipReportKeys.all,
      apiPath,
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.employees?.sort((a, b) => a.localeCompare(b)),
    ],
  };

  const employeeBreakReportKeys = {
    all: ['employee-break'] as const,
    list: (apiPath: string, request: EmployeeBreakReportCommonRequestSchema) => [
      ...employeeBreakReportKeys.all,
      apiPath,
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
    ],
  };

  const intervalSalesAndLaborKeys = {
    all: ['interval-sales-labor'] as const,
    list: (request: IntervalSalesAndLaborReportCommonRequestSchema) => [
      ...intervalSalesAndLaborKeys.all,
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.jobCodes?.sort((a, b) => a.localeCompare(b)),
      request.laborReportingGroup?.sort((a, b) => a.localeCompare(b)),
      request.granularity,
    ],
  };

  const employeeSalesPerformanceReportKeys = {
    all: ['employee-sales-performance'] as const,
    list: (request: EmployeeSalesPerformanceCommonRequest, apiPath: string) => [
      ...employeeSalesPerformanceReportKeys.all,
      apiPath,
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.jobcodes?.sort((a, b) => a.localeCompare(b)),
      request.dayParts,
    ],
  };

  const overtimeThresholdReportKeys = {
    all: ['approaching-overtime-threshold'] as const,
    list: (request: OvertimeThresholdCommonRequest, apiPath: string) => [
      ...overtimeThresholdReportKeys.all,
      apiPath,
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.hourThresholdLowerRange,
      request.hourThresholdHigherRange,
    ],
  };

  const acaThresholdReportKeys = {
    hourlyGrid: (request: ApproachingACAHourlyThresholdGridDataRequest) => [
      'approaching-aca-threshold',
      'hourly-threshold',
      'grid-data',
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
      request.employeeStatus,
      request.jobType,
      request.hourThresholdLowerRange,
      request.hourThresholdHigherRange,
    ],
    averageHoursGrid: (request: ApproachingACAHourlyThresholdGridDataRequest) => [
      'approaching-aca-threshold',
      'average-hours',
      'grid-data',
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
    ],
  };

  const genericPayrollExportKeys = {
    grid: (request: BaseReportDataRequest) => [
      'generic-payroll',
      'grid-data',
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a, b) => a.localeCompare(b)),
    ],
  };

  const jobcodeHistoryKeys = {
    all: ['jobcode-history', firebaseToken] as const,
    list: (request: JobcodeHistoryGridRequest) => [
      ...jobcodeHistoryKeys.all,
      request.employeeId,
      request.siteIds.sort((a, b) => a.localeCompare(b)),
    ],
  };

  const employeePayrollSummaryKeys = {
    all: ['employee-payroll'] as const,
    grid: (request: EmployeePayrollDto, apiPath: string) => [
      ...employeePayrollSummaryKeys.all,
      apiPath,
      'grid-data',
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a: string, b: string) => a.localeCompare(b)),
      request.jobCodeNames?.sort(),
    ],
    list: (request: EmployeePayrollDto, apiPath: string) => [
      ...employeePayrollSummaryKeys.all,
      apiPath,
      request.startDate,
      request.endDate,
      request.siteIds?.sort((a: string, b: string) => a.localeCompare(b)),
      request.jobCodeNames?.sort(),
    ],
  };

  const jobProfileKeys = {
    all: ['job-profiles', firebaseToken] as const,
    listById: (id: string) => [...jobProfileKeys.all, id],
    jobProfilesByEmployeeId: (empid: string) => [...jobProfileKeys.all, empid, 'job-profile'],
    jobProfilesByEmployeesIds: (empids: string[]) => [
      ...jobProfileKeys.all,
      empids?.sort((a, b) => a.localeCompare(b)),
      'job-profile',
    ],
    self: () => ['self' as const, ...jobProfileKeys.all],
  };

  const employeeSitesKeys = {
    all: ['employee-sites', firebaseToken] as const,
    listById: (id: string) => [...employeeSitesKeys.all, id],
    employeeSitesByEmployeeId: (empid: string) => [...employeeSitesKeys.all, empid, 'employee-sites'],
    employeeSitesByEmployeesIds: (empids: string[]) => [
      ...employeeSitesKeys.all,
      empids?.sort((a, b) => a.localeCompare(b)),
      'employee-sites',
    ],
    self: () => ['self' as const, ...employeeSitesKeys.all],
  };

  const tipDistributionKeys = {
    all: ['tip-distributions', siteId] as const,
    byDate: (businessDate: string) => [...tipDistributionKeys.all, businessDate] as const,
  };

  const tipConfigurationKeys = {
    all: ['tip-configuration', org?.bslId] as const,
  };

  const tipShareKeys = {
    all: ['tip-share', org?.bslId] as const,
    distribution: () => [...tipShareKeys.all, 'distribution'] as const,
    contribution: () => [...tipShareKeys.all, 'contribution'] as const,
    contributionPoolTotals: (businessDate: string) =>
      [...tipShareKeys.all, 'contribution-pool-totals', businessDate] as const,
  };

  const tipShareShiftKeys = {
    all: ['tip-share-shifts', org?.bslId, siteId] as const,
    detail: (shiftId: string) => [...tipShareShiftKeys.all, 'detail', shiftId] as const,
  };

  const bannerStateKeys = {
    all: ['banner-state', org?.bslId, siteId, firebaseToken] as const,
    byEmployee: (employeeId: string) => [...bannerStateKeys.all, 'employee', employeeId] as const,
    byEmployeeAndType: (employeeId: string, bannerType: string) =>
      [...bannerStateKeys.all, 'employee', employeeId, 'bannerType', bannerType] as const,
  };

  return {
    ...queryKeys,
    employee: employeeKeys,
    jobProfile: jobProfileKeys,
    employeeSites: employeeSitesKeys,
    employeeDetails: employeeDetailsKeys,
    employeeJobCodeAssignment: employeeJobCodeAssignmentKeys,
    genericPayrollExport: genericPayrollExportKeys,
    overtimeThresholdReport: overtimeThresholdReportKeys,
    employeeTipReport: employeeTipReportKeys,
    employeeBreakReport: employeeBreakReportKeys,
    intervalSalesAndLaborReport: intervalSalesAndLaborKeys,
    employeeSalesPerformanceReport: employeeSalesPerformanceReportKeys,
    acaThresholdReport: acaThresholdReportKeys,
    jobcodeHistory: jobcodeHistoryKeys,
    employeePayrollSummary: employeePayrollSummaryKeys,
    tipDistribution: tipDistributionKeys,
    tipConfiguration: tipConfigurationKeys,
    tipShare: tipShareKeys,
    tipShareShift: tipShareShiftKeys,
    bannerState: bannerStateKeys,
  };
};

export const useBspQueryKeys = () => {
  const provisioningKeys = {
    userByUserId: (ncrUserId: string) => ['provisioning-user', ncrUserId],
    usersByIds: (ncrUserIds: string[]) => ['provisioning-users', ...ncrUserIds.sort((a, b) => a.localeCompare(b))],
  };

  return { provisioning: provisioningKeys };
};

export default useLaborQueryKeys;
